/* eslint-disable formatjs/enforce-description */
import classNames from 'classnames';
import type {
  ChangeEvent,
  FunctionComponent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { ComponentWrapper } from '@trello/component-wrapper';
import { logoDomain } from '@trello/config';
import { ErrorBoundary } from '@trello/error-boundaries';
import { sendErrorEvent } from '@trello/error-reporting';
import { getNetworkError } from '@trello/graphql-error-handling';
import { intl } from '@trello/i18n';
import { localizeErrorCode } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { OrganizationIcon } from '@trello/nachos/icons/organization';
import { Popover, usePopover } from '@trello/nachos/popover';
import { WorkspaceDefaultLogo } from '@trello/workspace-logo';

import { l } from 'app/scripts/lib/localize';
import { Util } from 'app/scripts/lib/util';
import { Alerts as Alert } from 'app/scripts/views/lib/Alerts';
import { useDeleteProfileImageMutation } from './DeleteProfileImageMutation.generated';
import { useEditAvatarQuery } from './EditAvatarQuery.generated';
import { useUploadProfileImageMutation } from './UploadProfileImageMutation.generated';

import styles from './EditAvatar.less';

interface EditAvatarProps {
  orgId: string;
  name: string;
}

export const EditAvatar: FunctionComponent<EditAvatarProps> = ({
  orgId,
  name,
}) => {
  const { data } = useEditAvatarQuery({
    variables: { id: orgId },
    waitOn: ['MemberHeader', 'MemberBoards'],
  });
  const [uploadProfileImage] = useUploadProfileImageMutation();
  const [deleteProfileImage] = useDeleteProfileImageMutation();
  const { triggerRef, popoverProps, toggle, hide } =
    usePopover<HTMLButtonElement>();
  const [errorMessage, setError] = useState('');
  const [showPNG, setShowPNG] = useState(true);
  const [showDefaultAvatar, setShowDefaultAvatar] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const logoHash = data?.organization?.logoHash;
  const logoExt = showPNG ? 'png' : 'gif';

  const meId = data?.member?.id;

  const isEditable =
    meId &&
    !!data?.organization?.memberships.find(
      (member) => member.idMember === meId && member.memberType === 'admin',
    );

  const onClickChange = useCallback(() => {
    if (isEditable) {
      toggle();
    }
  }, [isEditable, toggle]);

  const onLogoError = useCallback(() => {
    showPNG ? setShowPNG(false) : setShowDefaultAvatar(true);
  }, [showPNG]);

  const onUploadClick = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      fileInput.current?.click();
    },
    [fileInput],
  );

  const onUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();

      setError('');

      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];

        if (!file) {
          return;
        }

        if (!Util.validFileSize(file)) {
          setError(l('images can not be larger than 10mb'));
          Analytics.sendTrackEvent({
            action: 'failed',
            actionSubject: 'fileInputUpload',
            source: 'changeTeamAvatarDialog',
            attributes: {
              reason: 'file too large',
            },
            containers: {
              organization: { id: orgId },
            },
          });
          return;
        }

        try {
          await uploadProfileImage({
            variables: {
              orgId,
              file,
            },
          });
          Analytics.sendTrackEvent({
            action: 'saved',
            actionSubject: 'fileInputUpload',
            source: 'changeTeamAvatarDialog',
            attributes: {
              reason: 'new file upload',
            },
            containers: {
              organization: { id: orgId },
            },
          });

          hide();
        } catch (err) {
          Analytics.sendTrackEvent({
            action: 'errored',
            actionSubject: 'fileInputUpload',
            source: 'changeTeamAvatarDialog',
            attributes: {
              // @ts-expect-error
              reason: err.message,
            },
            containers: {
              organization: { id: orgId },
            },
          });

          const networkError = getNetworkError(err);
          switch (networkError?.code) {
            case 'GM_ERROR':
              setError(localizeErrorCode('organization', 'GM_ERROR'));
              break;
            default:
              setError(localizeErrorCode('organization', 'UNKNOWN_ERROR'));
              sendErrorEvent(err, {
                tags: {
                  ownershipArea: 'trello-web-eng',
                  feature: 'Team Edit Avatar',
                },
                extraData: {
                  component: 'EditAvatar',
                },
                networkError,
              });
              break;
          }
        }
      }
    },
    [hide, orgId, uploadProfileImage],
  );

  const deleteLogo = useCallback(
    async (e: ReactMouseEvent) => {
      e.preventDefault();
      try {
        await deleteProfileImage({ variables: { orgId } });
        Analytics.sendTrackEvent({
          action: 'removed',
          actionSubject: 'fileInputUpload',
          source: 'changeTeamAvatarDialog',
          containers: {
            organization: { id: orgId },
          },
        });
      } catch (err) {
        const networkError = getNetworkError(err);
        sendErrorEvent(err, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Team Edit Avatar',
          },
          extraData: {
            component: 'EditAvatar',
          },
          networkError,
        });
        Alert.showLiteralText(
          localizeErrorCode('organization', 'UNKNOWN_ERROR'),
          'error',
          'alert',
          10000,
        );
      } finally {
        hide();
      }
    },
    [deleteProfileImage, hide, orgId],
  );

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-web-eng',
        feature: 'Team Edit Avatar',
      }}
    >
      <ComponentWrapper>
        <button
          className={classNames({
            [styles.container]: true,
            [styles.isEditable]: isEditable,
          })}
          ref={triggerRef}
          onClick={onClickChange}
        >
          {logoHash && !showDefaultAvatar ? (
            <img
              className={styles.profileImage}
              src={`${logoDomain}/${logoHash}/170.${logoExt}`}
              alt=""
              onError={onLogoError}
            />
          ) : (
            <WorkspaceDefaultLogo
              name={name}
              className={styles.workspaceDefaultLogo}
            />
          )}

          <span className={styles.profileImageText}>
            <FormattedMessage
              id="templates.org_detail.change-logo"
              defaultMessage="Change logo"
            />
          </span>
        </button>
        <Popover
          {...popoverProps}
          title={intl.formatMessage({
            id: 'templates.org_detail.change-logo',
            defaultMessage: 'Change logo',
          })}
        >
          <div>
            {!!errorMessage && <p className={styles.error}>{errorMessage}</p>}
            <div className={styles.uploader}>
              <form className={styles.realFile}>
                <input
                  data-testid="fileInput"
                  type="file"
                  name="file"
                  className={styles.hideInput}
                  onChange={onUpload}
                  ref={fileInput}
                />
              </form>
              <Button
                className={styles.popoverButton}
                onClick={onUploadClick}
                iconBefore={<OrganizationIcon />}
                shouldFitContainer
              >
                <FormattedMessage
                  id="templates.popover_org_logo.upload-a-new-logo"
                  defaultMessage="Upload a new logo"
                />
              </Button>
            </div>
            {logoHash && (
              <Button
                data-testid="deleteLogoButton"
                className={styles.popoverButton}
                onClick={deleteLogo}
              >
                <FormattedMessage
                  id="templates.popover_org_logo.remove-logo"
                  defaultMessage="Remove logo"
                />
              </Button>
            )}
          </div>
        </Popover>
      </ComponentWrapper>
    </ErrorBoundary>
  );
};
